import { apiRequest } from '@/shared/api/client'

import {
  appendTaskFeedFilter,
  type AvailableTaskCard,
  type AvailableTasksStatus,
  type ListAvailableTasksResponse,
  type ListTaskProgressesResponse,
  type MyActiveTaskProgress,
  type TaskFeedFilterParams,
  type TaskListTypeFilter
} from './watchTaskApi'

/** The fan feed a platform of a group belongs to; absent when none. */
export type TaskGroupList = 'available' | 'soon' | 'my' | 'closed'

export type TaskGroupPlatformCard = {
  task: AvailableTaskCard
  list?: TaskGroupList
  progress?: MyActiveTaskProgress
  /** The link of the fan's latest attempt that had one, to prefill a retry. */
  last_submitted_url?: string
}

export type TaskGroupCard = {
  /** The task id for a task written before groups. */
  group_id: string
  type: string
  /** The group's first visible task: title, copy, cover, brand and media. */
  brief: AvailableTaskCard
  platforms: TaskGroupPlatformCard[]
}

export type ListTaskGroupsResponse = {
  groups: TaskGroupCard[]
  /** Groups, not platforms. */
  total: number
  available_platforms?: string[]
}

type ListTaskGroupsParams = TaskFeedFilterParams & {
  page?: number
  page_size?: number
  type?: TaskListTypeFilter
  /** Any-of type filter (ANDed with `type`). */
  types?: readonly TaskListTypeFilter[]
  status?: AvailableTasksStatus
}

export async function getTaskGroups(
  list: 'available' | 'my' | 'closed',
  params: ListTaskGroupsParams
) {
  const qs = new URLSearchParams()
  if (params.page !== undefined) {
    qs.set('page', String(params.page))
  }
  if (params.page_size !== undefined) {
    qs.set('page_size', String(params.page_size))
  }
  if (params.type) {
    qs.set('type', params.type)
  }
  for (const type of params.types ?? []) {
    qs.append('types', type)
  }
  if (params.status) {
    qs.set('status', params.status)
  }
  appendTaskFeedFilter(qs, params)
  const suffix = qs.toString()
  return apiRequest<ListTaskGroupsResponse>({
    path: `/api/v2/task-groups/${list}${suffix ? `?${suffix}` : ''}`,
    method: 'GET',
    format: 'json'
  })
}

export async function getTaskGroupByTask(taskId: string) {
  return apiRequest<TaskGroupCard>({
    path: `/api/v2/tasks/${encodeURIComponent(taskId)}/group`,
    method: 'GET',
    format: 'json'
  })
}

/** Cards carry their group so the mappers can sum and badge every platform. */
export type WithTaskGroup = { task_group?: TaskGroupCard }

const ACTION_NEEDED = new Set(['moderation_error', 'reserved'])

/** The platform a card opens: one waiting for the fan first, then the first listed. */
export function leadTaskGroupPlatform(
  group: TaskGroupCard
): TaskGroupPlatformCard | undefined {
  return (
    group.platforms.find(p => ACTION_NEEDED.has(p.progress?.state ?? '')) ??
    group.platforms.find(p => p.list === 'my') ??
    group.platforms[0]
  )
}

/** A groups page shaped as the available-tasks page the feed already pages through. */
export function taskGroupsAsAvailablePage(
  page: ListTaskGroupsResponse
): ListAvailableTasksResponse {
  const tasks: (AvailableTaskCard & WithTaskGroup)[] = []
  for (const group of page.groups) {
    const lead = leadTaskGroupPlatform(group)
    if (lead) {
      tasks.push({ ...lead.task, task_group: group })
    }
  }
  return {
    tasks,
    total: page.total,
    available_platforms: page.available_platforms
  }
}

/** A groups page shaped as the task-progress page of My / Closed. */
export function taskGroupsAsProgressPage(
  page: ListTaskGroupsResponse
): ListTaskProgressesResponse {
  const progresses: (MyActiveTaskProgress & WithTaskGroup)[] = []
  for (const group of page.groups) {
    const lead = leadTaskGroupPlatform(group)
    if (lead?.progress) {
      progresses.push({ ...lead.progress, task_group: group })
    }
  }
  return {
    task_progresses: progresses,
    total: page.total,
    available_platforms: page.available_platforms
  }
}
